<script setup lang="ts">
import { localize, openInNewTab } from "@/utils";
import { reactive, ref } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Swiper as SwiperClass } from "swiper/types";
import { Navigation, Pagination, Autoplay } from "swiper/modules";

import HomeHeader from "@/views/home/HomeHeader.vue";
import HomeFooter from "@/views/home/HomeFooter.vue";

const textSwiperRef = ref<SwiperClass | null>(null);
const scrollbarSwiperRef = ref<SwiperClass | null>(null);

// window.scrollTo(0, 0);
const state = reactive({
  tabIndex: 0,
  textSwiperIndex: 0,
  scrollbarIndex: 0,
  autoplay: true,
});
const textSwiperEvent = {
  init: function (swiper: SwiperClass) {
    textSwiperRef.value = swiper;
  },
  slideChange: function (swiper: SwiperClass) {
    state.textSwiperIndex = swiper.realIndex;
  },
};
const scrollbarSwiperEvent = {
  init: function (swiper: SwiperClass) {
    scrollbarSwiperRef.value = swiper;
    swiper.autoplay.start();
  },
  slideChange: function (swiper: SwiperClass) {
    state.scrollbarIndex = swiper.realIndex;
  },
};

function slideTextSwiper(index: number) {
  textSwiperRef.value?.slideToLoop(index);
}
function slideScrollbarSwiper(index: number) {
  scrollbarSwiperRef.value?.slideToLoop(index);
}
function toggleAutoPlay() {
  state.autoplay = !state.autoplay;
  // console.log(scrollbarSwiperRef.value?.autoplay);
  if (state.autoplay) {
    scrollbarSwiperRef.value?.autoplay.start();
  } else {
    scrollbarSwiperRef.value?.autoplay.stop();
  }
}
</script>

<template>
  <div id="wrap">
    <home-header />

    <!-- container -->
    <main id="ct">
      <!-- 페이지 시작 -->
      <div class="illust-intro">
        <div class="container">
          <h1 class="heading">
            R2<br class="d-block d-lg-none" />
            Planner
            <b>Gurus</b>
          </h1>
          <span>{{ localize("당신의 지식과 경험을 나누세요.")}}</span>
          <p
            class="fs-4"
            v-html="
              localize('디지털 진단과 치료 계획 수립, 전문적인 보철 디자인 서비스를 통해<br />전 세계 임상의들과 함께하세요.<br />R2 Planner는 단순한 서비스 제공자를 넘어,<br />임상가에게 힘이 되는 든든한 파트너이자 멘토입니다.')
            "
          />
          <div class="illust">
            <img src="@/assets/home/img/playground/r2planner-illust.png" alt="" />
          </div>
        </div>
      </div>
      <div class="container">
        <ul class="nav nav-tabs r2-tabs">
          <li class="nav-item">
            <a data-toggle="tab" class="nav-link" @click="state.tabIndex = 0" :class="{ active: state.tabIndex == 0 }"
              >R2 Planner Service</a
            >
          </li>
          <li class="nav-item">
            <a
              data-toggle="tab"
              class="nav-link"
              @click="state.tabIndex = 1"
              :class="{ active: state.tabIndex == 1 }"
              >{{ localize("R2 Planner 소개") }}</a
            >
          </li>
        </ul>
        <div class="tab-content">
          <div id="tab01" class="tab-pane" :class="{ active: state.tabIndex == 0 }">
            <section class="sect">
              <div class="subtitle-wrap">
                <h2 class="font-poppins">R2 Planner Service</h2>
                <p
                  v-html="
                    localize('지금, R2 Planner의 폭넓은 디지털 플래닝 및<br/>디자인 서비스를 경험해보세요.')
                  "
                />
              </div>
              <ul class="swiper-nav">
                <li :class="{ active: state.textSwiperIndex == 0 }" @click="slideTextSwiper(0)">
                  <i class="icon-r2-implant-planning"></i>R2 Implant Planning
                </li>
                <li :class="{ active: state.textSwiperIndex == 1 }" @click="slideTextSwiper(1)">
                  <i class="icon-digital-oral-design"></i>R2stuidoQ & QVD
                </li>
                <li :class="{ active: state.textSwiperIndex == 2 }" @click="slideTextSwiper(2)">
                  <i class="icon-chair-side"></i>Chair-side<br />CAD/CAM Design
                </li>
                <!-- <li :class="{ active: state.textSwiperIndex == 3 }" @click="slideTextSwiper(3)">
                  <i class="icon-meg-aligner-planning"></i>Meg Aligner planning
                </li> -->
                <li :class="{ active: state.textSwiperIndex == 3 }" @click="slideTextSwiper(3)">
                  <i class="icon-faceguide-planning"></i>FaceGIDE planning
                </li>
                <li :class="{ active: state.textSwiperIndex == 4 }" @click="slideTextSwiper(4)">
                  <i class="icon-mxfacial"></i>Mx.Facial &<br />Plastic Reconstruction
                </li>
              </ul>

              <swiper
                class="text-pagination-slider"
                :loop="true"
                :observer="true"
                :observe-parents="true"
                :modules="[Navigation, Pagination]"
                :navigation="{
                  nextEl: '.swiper-button-next',
                  prevEl: '.swiper-button-prev',
                }"
                :pagination="{
                  el: '.swiper-pagination',
                  type: 'bullets',
                }"
                @init="textSwiperEvent.init"
                @slideChange="textSwiperEvent.slideChange"
              >
                <swiper-slide>
                  <div class="swiper-img">
                    <img src="@/assets/home/img/playground/r2planner1-1.png" alt="" />
                  </div>
                  <div class="pagination-slide-txt">
                    <h3>R2 Implant Planning</h3>
                    <p
                      v-html="
                        localize('R2GATE를 기반으로 한 임플란트 플래닝과 수술 가이드<br />및 임시 보철 디자인 서비스를 제공합니다.')
                      "
                    />
                    <div class="list-wrap">
                      <span>What's included</span>
                      <ul class="list-dot"> 
                        <li>{{ localize("CBCT와 STL 매칭") }}</li>
                        <li>R2 Guide Design</li>
                        <li>{{ localize("진단 Wax-up") }}</li>
                        <li>{{ localize("ONE-DAY teeth (템포라리) 디자인") }}</li>
                        <li>{{ localize("임플란트 진단 (포지셔닝)") }}</li>
                        <li>{{ localize("Digital Eye 골질 분석") }}</li>
                      </ul>
                    </div>
                    <!-- <button class="btn btn-primary mt-4 d-flex justify-content-center">{{ localize("주문하기") }}</button> -->
                  </div>
                </swiper-slide>
                <swiper-slide>
                  <div class="swiper-img">
                    <img src="@/assets/home/img/playground/r2planner1-2.png" alt="" />
                  </div>
                  <div class="pagination-slide-txt">
                    <h3>Q.V.D</h3>
                    <p
                      v-html="
                        localize('무치악 환자 치료를 위해 덴탈 아바타를 기반으로<br/>이상적인 V.D 설정과 치료 계획을 제안 해드립니다.')
                      "
                    />
                    <div class="list-wrap">
                      <span>What's included</span>
                      <ul class="list-dot">
                        <li>{{ localize("Dental Avatar 생성") }}</li>
                        <li>Implant planning</li>
                        <li>{{ localize("Ai Cephalomatric 분석") }}</li>
                        <li>Temporary design</li>
                        <li>{{ localize("Vertical Dimension 결정") }}</li>
                        <li>{{ localize("CAD 작업을 위한 STL 제공") }}</li>
                        <li>{{ localize("진단 Wax-up") }}</li>
                      </ul>
                      <p class="mt-2 text-muted fsmall"
                        v-html="
                          localize('⊛ QVD 서비스는 R2studioQ 사용자에게 우선 제공되며, 일반 사용자에게는 서비스가 제한 될 수<br/>있습니다.')
                        "
                      />
                    </div>
                    <!-- <button class="btn btn-primary mt-4 d-flex justify-content-center">{{ localize("주문하기") }}</button> -->
                  </div>
                </swiper-slide>
                
                <swiper-slide>
                  <div class="swiper-img">
                    <img src="@/assets/home/img/playground/r2planner1-3.png" alt="" />
                  </div>
                  <div class="pagination-slide-txt">
                    <h3>Chair-side<br/>CAD/CAM Design</h3>
                    <p
                      v-html="
                        localize('치과 진료에 필요한 다양한 보철 디자인 서비스를 제공합니다.')
                      " 
                    />
                    <div class="list-wrap">
                      <span>What's included</span>
                      <ul class="list-dot no-grid">
                        <li>{{ localize('아나토믹 크라운 디자인 (임시치아 및 최종 보철용)') }}</li>
                        <li>{{ localize('임플란트를 위한 맞춤형 지대주 디자인') }}</li>
                        <li>{{ localize('Crown & Bridge 디자인') }}</li>
                        <li>{{ localize('인레이, 온레이 디자인') }}</li>
                      </ul>
                      <p class="mt-2 text-muted fsmall">
                        {{ localize("⊛ R2 플래너에 따라 제공되는 서비스가 다를 수 있습니다.") }}
                      </p>
                    </div>
                    <!-- <button class="btn btn-primary mt-4 d-flex justify-content-center">{{ localize("주문하기") }}</button> -->
                  </div>
                </swiper-slide>
                <!-- <swiper-slide>
                  <div class="swiper-img">
                    <img src="@/assets/home/img/playground/r2planner1-4.png" alt="" />
                  </div>
                  <div class="pagination-slide-txt">
                    <h3>Meg Aligner planning</h3>
                    <p>
                      {{ localize("심미 투명 교정을 위한 치아 셑업 파일을 제공합니다.") }}
                    </p>
                    <div class="list-wrap">
                      <span>What's included</span>
                      <ul class="list-dot no-grid">
                        <li>{{ localize("치아 Setup Step 파일") }}</li>
                      </ul>
                    </div>
                  </div>
                </swiper-slide> -->
                <swiper-slide>
                  <div class="swiper-img">
                    <img src="@/assets/home/img/playground/r2planner1-5.png" alt="" />
                  </div>
                  <div class="pagination-slide-txt">
                    <h3>FaceGIDE Planning</h3>
                    <p
                      v-html="
                        localize('FaceGIDE(디지털 양악 수술 솔루션)를 위한<br/>Setup Plan과 수술에 필요한 Saw Guide,<br/>환자 맞춤형 플레이트 디자인을 제공합니다.')
                      "
                    />
                    <span style="font-size: 15px; font-weight: 500;">
                      {{ localize("⊛ Setup 및 디자인 파일은 전용 앱인 FaceGIDE Lite에서 확인하실 수 있습니다.") }}
                    </span>
                    <div class="list-wrap">
                      <span>What's included</span>
                      <ul class="list-dot no-grid">
                        <li>{{ localize("양악 수술을 위한 분석") }}</li>
                        <li>{{ localize("Jaw segmentation 및 setup (프로젝트 파일)") }}</li>
                        <li>{{ localize("Saw-guide 디자인 파일 (STL)") }}</li>
                        <li>{{ localize("환자 맞춤형 Plate 디자인 파일 (STL)") }}</li>
                      </ul>
                      <p class="mt-2 text-muted fsmall">
                        {{ localize("⊛ FaceGIDE 서비스는 국내(대한민국)에서는 완제품 형태로만 제공됩니다.") }}
                      </p>
                    </div>
                    <div class="d-flex">
                      <a href="https://playgroundmaintenance.blob.core.windows.net/webstaticassets/FaceGIDE/FACEGIDE Order GuideBook_2024ver.pdf" class="btn btn-primary mt-4 d-flex justify-content-center mr-4" target="_blank" rel="noopener noreferrer">{{ localize("주문 가이드 보기") }}</a>
                      <!-- <button class="btn btn-primary mt-4 d-flex justify-content-center mr-4">{{ localize("주문 가이드 보기") }}</button> -->
                      <!-- <button class="btn btn-primary mt-4 d-flex justify-content-center">{{ localize("주문하기") }}</button> -->
                    </div>
                  </div>
                </swiper-slide>
                <swiper-slide>
                  <div class="swiper-img">
                    <img src="@/assets/home/img/playground/r2planner1-6.png" alt="" />
                  </div>
                  <div class="pagination-slide-txt">
                    <h3>Mx.Facial &<br />Plastic Reconstruction</h3>
                    <p
                      v-html="
                        localize('선천성 기형, 외상, 질병 등으로 손상되거나 손실된<br/>악안면 구조의 복원을 위한 맞춤형 보형물 디자인을 제공합니다.')
                      "
                    />
                    <span style="font-size: 15px; font-weight: 500;">
                      {{ localize("⊛ 디자인 파일은 전용 앱인 FaceGIDE Lite에서 확인하실 수 있습니다.") }}
                    </span>
                    <div class="list-wrap">
                      <span>What's included</span>
                      <ul class="list-dot no-grid">
                        <li>{{ localize("악안면 분석 (비대칭 및 결손 구조 확인)") }}</li>
                        <li>{{ localize("환자 맞춤형 보형물 디자인") }}</li>
                        <li>{{ localize("보형물 식립을 위한 가이드 및 플레이트 디자인") }}</li>
                      </ul>
                      <p class="mt-2 text-muted fsmall">
                        {{ localize("⊛ 악안면 보형물 서비스는 국내(대한민국)에서는 완제품 형태로만 제공됩니다.") }}
                      </p>
                    </div>
                    <div class="d-flex">
                      <!-- <button class="btn btn-primary mt-4 d-flex justify-content-center mr-4">{{ localize("주문 가이드 보기") }}</button>
                      <button class="btn btn-primary mt-4 d-flex justify-content-center">{{ localize("주문하기") }}</button> -->
                    </div>
                  </div>
                </swiper-slide>
                <div class="swiper-pagination pagination_bullet"></div>
                <div class="swiper-controls type-line">
                  <div class="swiper-button-prev"></div>
                  <div class="swiper-button-next"></div>
                </div>
              </swiper>
            </section>
          </div>

          <div id="tab02" class="tab-pane" :class="{ active: state.tabIndex == 1 }">
            <div class="subtitle-wrap">
              <h2 class="font-poppins">Share Your Insight</h2>
              <p
                v-html="
                  localize('지식은 나눌수록 더 큰 가치를 가집니다.<br />당신의 능력과 경험을 전 세계의 Clinician과 함께하세요.')
                "
              />
              <p
                class="desc"
                v-html="
                  localize('R2 Planner는 전문 지식과 임상 경험을 바탕으로, 다양한 임상 케이스에 대해<br/>진단 및 맞춤형 디자인 서비스를 제공합니다. Dental Technician과 Clinician 누구나<br/>소정의 절차를 거쳐 R2 Planner로 활동하실 수 있습니다.')
                "
              />
            </div>
            <div class="timeline">
              <ul>
                <li>
                  <img src="@/assets/home/img/playground/r2planner3-1.png" alt="" />
                  <div class="btn-desc">
                    <h3>R2 Player Sign in</h3>
                    <p
                      v-html="
                        localize('R2 Planner가 되기 위한 첫걸음,<br/>R2 Playground에서 R2 Player로 가입해 주세요.')
                      "
                    />
                    <router-link to="/home/join" class="btn btn-primary"
                      >Sign in<i class="icon icon-arrow"></i
                    ></router-link>
                  </div>
                </li>
                <li>
                  <img src="@/assets/home/img/playground/r2planner3-2.png" alt="" />
                  <div class="btn-desc" style="text-align: left;">
                    <h3>R2 Academy</h3>
                    <p
                      v-html="
                        localize('R2 Planner가 되기 위한 다양한 교육 컨텐츠가 준비 되어 있습니다.')
                      "
                    />
                    <p
                      class="m-0"
                      v-html="
                        localize('최신 업데이트에 대한 소개와 tutorial로 항상 최신 지식을 유지하실 수 있습니다.')
                      "
                    />
                    <!-- <a @click="openInNewTab('/academy')" class="btn btn-primary"
                      >{{ localize('설립중')}}<i class="icon icon-arrow"></i
                    ></a> -->
                  </div>
                </li>
                <li>
                  <img src="@/assets/home/img/playground/r2planner3-3.png" alt="" />
                  <div class="btn-desc">
                    <h3>R2 Planner</h3>
                    <p
                      v-html="localize('R2 planner의 활동이 궁금하세요?<br />기존 R2 planner들의 활동을 살펴 보세요.')"
                    />
                    <router-link to="/home/playground/planner" class="btn btn-primary" style="pointer-events: none;" @click.prevent="false"
                      >{{ localize("준비 중") }}<i class="icon icon-arrow"></i
                    ></router-link>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <!-- slider -->
          <!-- <section class="sect text-center scrollbar-slide-wrap">
            <h2 class="mb-4">{{ localize("R2 Player에서 R2 Planner 되기") }}</h2>
            <a @click="openInNewTab('/academy')" class="btn btn-outline"
              >R2 Academy<i class="icon icon-arrow-black"></i
            ></a>
            <swiper
              class="scrollbar-slide"
              :autoplay="{
                delay: 3000,
              }"
              :loop="true"
              :observer="true"
              :observe-parents="true"
              :modules="[Navigation, Pagination, Autoplay]"
              :navigation="{
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
              }"
              :pagination="{
                el: '.swiper-pagination-bar',
              }"
              @init="scrollbarSwiperEvent.init"
              @slideChange="scrollbarSwiperEvent.slideChange"
            >
              <swiper-slide>
                <img src="@/assets/home/img/playground/r2planner2-1.png" />
                <div class="slider-txt">
                  <strong><span class="bullet">1</span> {{ localize("정규 교육 수강") }}</strong>
                  <p v-html="localize('R2 Planner Academy에서<br />정규 교육을 수강하세요.')" />
                </div>
              </swiper-slide>
              <swiper-slide>
                <img src="@/assets/home/img/playground/r2planner2-2.png" />
                <div class="slider-txt">
                  <strong><span class="bullet">2</span> {{ localize("자격 시험 응시") }}</strong>
                  <p v-html="localize('R2 Planner 자격 시험에 응시하여<br />최종 합격하세요.')" />
                </div>
              </swiper-slide>
              <swiper-slide>
                <img src="@/assets/home/img/playground/r2planner2-3.png" />
                <div class="slider-txt">
                  <strong><span class="bullet">3</span> {{ localize("R2 Planner 인증 신청") }}</strong>
                  <p v-html="localize('R2 Planner 인증을 신청하고<br />심사를 통과하세요.')" />
                </div>
              </swiper-slide>
              <swiper-slide>
                <img src="@/assets/home/img/playground/r2planner2-4.png" />
                <div class="slider-txt">
                  <strong><span class="bullet">4</span> {{ localize("R2 Planner 활동 시작") }}</strong>
                  <p v-html="localize('축하합니다!<br />R2 Planner 활동을 시작하세요')" />
                </div>
              </swiper-slide>
              <div class="swiper-controls type-line">
                <button :class="{ on: !state.autoplay }" @click="toggleAutoPlay" class="btn-autoplay"></button>
                <div class="swiper-button-prev"></div>
                <div class="swiper-button-next"></div>
              </div>
              <div class="swiper-pagination-bar"></div>
            </swiper>
            <ul class="swiper-txt-btn">
              <li :class="{ active: state.scrollbarIndex == 0 }" @click="slideScrollbarSwiper(0)">
                {{ localize("정규 교육 수강") }}
              </li>
              <li :class="{ active: state.scrollbarIndex == 1 }" @click="slideScrollbarSwiper(1)">
                {{ localize("자격 시험 응시") }}
              </li>
              <li :class="{ active: state.scrollbarIndex == 2 }" @click="slideScrollbarSwiper(2)">
                {{ localize("R2 Planner 인증 신청") }}
              </li>
              <li :class="{ active: state.scrollbarIndex == 3 }" @click="slideScrollbarSwiper(3)">
                {{ localize("R2 Planner 활동 시작") }}
              </li>
            </ul>
          </section> -->
        </div>
      </div>
      <!-- //container -->
    </main>
    <!-- footer -->
    <home-footer />
    <!-- //footer -->

    <div class="bg-blur"></div>
  </div>
</template>

<style scoped></style>
